import React, { useState, useEffect } from 'react';

type TemplateComponent = React.ComponentType<any>;

const normalizeTemplateName = (name: string | number): string => {
  const nameStr = String(name);
  let result = nameStr
    .trim()
    .replaceAll(/\s+/g, '')
    .replaceAll(/[^a-zA-Z0-9_-]/g, '');

  while (result.startsWith('-')) {
    result = result.slice(1);
  }

  while (result.endsWith('-')) {
    result = result.slice(0, -1);
  }

  return result;
};

export const useTemplateLoader = (
  componentBaseName: string,
  templateName: string | number,
  defaultTemplate: TemplateComponent,
  enabled: boolean = true
): TemplateComponent => {
  const [loadedTemplate, setLoadedTemplate] = useState<TemplateComponent | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoadedTemplate(() => defaultTemplate);
      return;
    }

    const templateNameStr = String(templateName).toLowerCase();
    if (templateNameStr === 'default') {
      setLoadedTemplate(() => defaultTemplate);
      return;
    }

    let cancelled = false;

    const loadTemplate = async () => {

      try {
        const normalizedName = normalizeTemplateName(templateName);
        if (!normalizedName) {
          if (!cancelled) setLoadedTemplate(() => defaultTemplate);
          return;
        }

        const module = await import(`@/components/specifics/${componentBaseName}_${normalizedName}.tsx`);
        if (!cancelled) {
          setLoadedTemplate(() => module.default || defaultTemplate);
        }
      } catch (error) {
        console.error(`[useTemplateLoader] ERROR caught:`, error);
         if (!cancelled) {
          setLoadedTemplate(() => defaultTemplate);
        }
      }
    };

    loadTemplate();

    return () => {
      cancelled = true;
    };
  }, [componentBaseName, templateName, enabled]);

  const TemplateWrapper = React.useMemo(() => {
    return (props: any) => {
      if (!loadedTemplate) {
        return React.createElement('div', {
          className: "text-gray-500 text-sm animate-pulse"
        }, 'Chargement template...');
      }

      return React.createElement(loadedTemplate, props);
    };
  }, [loadedTemplate]);
  
  return TemplateWrapper;
};